// @owners { team: patient-health }
import { SPACING } from '@alto/design-library-tokens';
import {
  addDays,
  addHours,
  addMilliseconds,
  addMinutes,
  addWeeks,
  endOfWeek,
  format,
  getMinutes,
  isAfter,
  isBefore,
  setMilliseconds,
  setSeconds,
  startOfDay,
  startOfWeek,
  subWeeks,
} from 'date-fns';
import { Dimensions } from 'react-native';
import { RRule, type Weekday } from 'rrule';
import { DAYS, RECURRENCE_SELECTION_KEYS } from './constants';
import { type Reminder, type ReminderSequence } from '~shared/types';

export const timeSinceMidnight = (value: number) => {
  return addMilliseconds(startOfDay(new Date()), value);
};

export const displayStartTime = (value?: number): string => format(timeSinceMidnight(value || 0), 'p');

export const displayRecurrence = (value?: string): string => {
  const options = RRule.parseString(value || '');

  if (options.freq === RRule.WEEKLY) {
    // @ts-expect-error TS(2533): Object is possibly 'null' or 'undefined'.
    const dow = options.byweekday.slice().sort((a, b) => a.weekday - b.weekday);

    // indexOf(RRule.SU) === 6 (last index)
    // move sunday to front of list for ui consistency
    if (dow.includes(RRule.SU)) {
      dow.pop();
      dow.unshift(RRule.SU);
    }

    // @ts-expect-error TS(7006): Parameter 'day' implicitly has an 'any' type.
    return dow.map((day) => DAYS[day.weekday]).join(', ');
  }

  if (options.freq === RRule.DAILY) {
    if (options.interval === 1) {
      return 'Daily';
    }

    if (options.interval === 2) {
      return 'Every other day';
    }

    return `Every ${options.interval} days`;
  }

  return '';
};

export type RecurrenceParts = {
  recurrence: string | null | undefined;
  daysOfWeek: number[];
  interval: number | null | undefined;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getRecurrenceParts = (recurrence: string | null | undefined): RecurrenceParts => {
  let recurrenceKey = null;
  let daysOfWeek: number[] = [];
  let interval = null;

  if (recurrence) {
    const options = RRule.parseString(recurrence);

    if (options.freq === RRule.DAILY) {
      if (options.interval === 1) {
        recurrenceKey = RECURRENCE_SELECTION_KEYS.daily;
      } else if (options.interval ?? 0 > 1) {
        recurrenceKey = RECURRENCE_SELECTION_KEYS.day_interval;

        interval = options.interval;
      }
    } else if (options.freq === RRule.WEEKLY) {
      recurrenceKey = RECURRENCE_SELECTION_KEYS.dow;
      let byweekday: Weekday[];
      if (Array.isArray(options.byweekday)) {
        byweekday = options.byweekday as Weekday[];
      } else {
        byweekday = options.byweekday ? [options.byweekday as Weekday] : [];
      }

      daysOfWeek = byweekday
        .slice()
        .sort((a, b) => a.weekday - b.weekday)
        .map((day) => day.weekday);
    }
  }

  return {
    recurrence: recurrenceKey,
    daysOfWeek,
    interval,
  };
};

export const getRecurrenceRRuleString = (
  { recurrence, daysOfWeek, interval }: RecurrenceParts,
  startDate: Date,
): string => {
  let freq;
  let byweekday;
  let rruleInterval = interval;

  switch (recurrence) {
    case RECURRENCE_SELECTION_KEYS.daily:
      freq = RRule.DAILY;
      rruleInterval = 1;
      break;

    case RECURRENCE_SELECTION_KEYS.day_interval:
      freq = RRule.DAILY;
      break;

    case RECURRENCE_SELECTION_KEYS.dow:
      freq = RRule.WEEKLY;
      byweekday = daysOfWeek;
      break;

    default:
      break;
  }

  return new RRule({
    freq,
    dtstart: startDate,
    // @ts-expect-error TS(2322): Type 'number | null | undefined' is not assignable to type 'number | undefined'.
    interval: rruleInterval,
    byweekday,
  }).toString();
};

export const getStartDate = (startTime: number | null | undefined): Date => {
  const now = new Date();
  let today = startOfDay(new Date());

  // 0 is false-y but a valid value in this case
  if ((startTime || startTime === 0) && isBefore(addHours(timeSinceMidnight(startTime), 1), now)) {
    today = addDays(today, 1);
  }

  return today;
};

export const latestReminder = (reminderSequence: ReminderSequence): Reminder | null | undefined => {
  const { reminders } = reminderSequence;

  if (reminders.length > 0) {
    return reminders.reduce((latest, reminder) => {
      const isLaterDate = isAfter(new Date(reminder.created_at), new Date(latest.created_at));
      return isLaterDate ? reminder : latest;
    }, reminders[0]);
  }

  return null;
};

export function makeOccurrenceID(
  reminderSequenceID: number,
  start: Date,
  duration: number,
  gcnSeqno: string | null | undefined,
  ndc: string | null | undefined,
): string {
  if (!(gcnSeqno || ndc)) {
    throw new Error('Either gcn or ndc is required');
  }

  return `${reminderSequenceID}:${start.valueOf()}:${duration}:${gcnSeqno || ndc}`;
}

const { width } = Dimensions.get('window');

const WEEK_WIDTH = width;
const CIRCLE_MARGINS_WIDTH = SPACING.STATIC.XS.value * 7 * 2;

// the length of one side of a Day circle based on screen size
export const CIRCLE_SIDE_LENGTH = Math.ceil((WEEK_WIDTH - CIRCLE_MARGINS_WIDTH) / 7);

export function reminderCalendarBounds(): {
  min: Date;
  max: Date;
} {
  const now = startOfWeek(new Date());

  return {
    min: subWeeks(now, 12),
    max: endOfWeek(addWeeks(now, 1)),
  };
}

// Returns closest 15 minute increment in the future, ignoring seconds
export function getDefaultTime(time: Date) {
  const timeMinutes = getMinutes(time);
  const closestIncrement = Math.ceil(timeMinutes / 15) * 15;
  const newTime = setMilliseconds(setSeconds(time, 0), 0);
  return addMinutes(newTime, closestIncrement - timeMinutes);
}
